import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { IconChevron } from './icons';

export function Section({ title, children, right, className = '', icon }: { title?: ReactNode; children: ReactNode; right?: ReactNode; className?: string; icon?: ReactNode }) {
  return (
    <section className={`section ${className}`}>
      {title && (
        <header className="section-head">
          <h2>{icon}{title}</h2>
          <span className="rule" aria-hidden="true" />
          {right}
        </header>
      )}
      {children}
    </section>
  );
}

/** Full-height column frame of a routed screen: `ScreenTop` stays put, `ScrollArea` regions scroll. The document itself never scrolls. */
export function Screen({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`screen ${className}`}>{children}</div>;
}

/** Non-scrolling top part of a screen (page header, stats, hint banner, tabs, filters, search field). */
export function ScreenTop({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`screen-top ${className}`}>{children}</div>;
}

/**
 * Bounded scroll region. By default it fills the rest of the screen (flex:1); with `maxHeight` it is an inline list
 * of that height. A soft fade at the top / bottom edge shows that more content is hidden there.
 */
export const ScrollArea = forwardRef<HTMLDivElement, { children: ReactNode; className?: string; maxHeight?: number | string; style?: CSSProperties; label?: string }>(
  function ScrollArea({ children, className = '', maxHeight, style, label }, fwd) {
    const ref = useRef<HTMLDivElement>(null);
    useImperativeHandle(fwd, () => ref.current as HTMLDivElement);
    const measure = useCallback(() => {
      const el = ref.current;
      if (!el) return;
      el.classList.toggle('can-up', el.scrollTop > 2);
      el.classList.toggle('can-down', el.scrollHeight - el.clientHeight - el.scrollTop > 2);
    }, []);
    useEffect(() => {
      const el = ref.current;
      if (!el) return;
      let raf = 0;
      const later = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(measure); };
      measure();
      const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(later) : null;
      ro?.observe(el);
      const mo = new MutationObserver(later);
      mo.observe(el, { childList: true, subtree: true, characterData: true });
      return () => { cancelAnimationFrame(raf); ro?.disconnect(); mo.disconnect(); };
    }, [measure]);
    return (
      <div ref={ref} className={`scroll-area ${maxHeight !== undefined ? 'bounded' : ''} ${className}`} style={maxHeight !== undefined ? { maxHeight, ...style } : style}
        onScroll={measure} aria-label={label}>
        {children}
      </div>
    );
  },
);

export type Tone = 'gold' | 'blue' | 'red' | 'green';

/** Sticker card. `tone` tints it (gold = notable, blue = info, red = danger, green = good). */
export function Card({ children, className = '', onClick, as: As = 'div', tone }: { children: ReactNode; className?: string; onClick?: () => void; as?: 'div' | 'article' | 'li'; tone?: Tone }) {
  return <As className={`card ${onClick ? 'tappable' : ''} ${tone ? `tone-${tone}` : ''} ${className}`} onClick={onClick}>{children}</As>;
}

/** Card with a built-in header (icon + title + optional right slot). */
export function Panel({ title, icon, right, tone, children, className = '' }: { title?: ReactNode; icon?: ReactNode; right?: ReactNode; tone?: Tone; children: ReactNode; className?: string }) {
  return (
    <section className={`panel ${tone ? `tone-${tone}` : ''} ${className}`}>
      {(title || icon || right) && (
        <header className="panel-head">
          {icon}
          <h3>{title}</h3>
          {right}
        </header>
      )}
      {children}
    </section>
  );
}

/** Big page title with an optional gold icon tile on the left and a slot on the right. */
export function PageHeader({ title, subtitle, icon, right }: { title: ReactNode; subtitle?: ReactNode; icon?: ReactNode; right?: ReactNode }) {
  return (
    <div className="page-head">
      {icon && <span className="ph-icon" aria-hidden="true">{icon}</span>}
      <div className="ph-text">
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {right && <div className="ph-right">{right}</div>}
    </div>
  );
}

/** Rounded stat pill: icon + small label + big value. `trend` colours the value (pos = green, neg = red). */
export function Stat({ icon, label, value, trend, title }: { icon?: ReactNode; label?: ReactNode; value: ReactNode; trend?: 'pos' | 'neg'; title?: string }) {
  return (
    <span className={`stat ${trend ?? ''}`} title={title}>
      {icon}
      <span>
        {label && <span className="stat-label">{label}</span>}
        <span className="stat-value num">{value}</span>
      </span>
    </span>
  );
}
/** Alias of Stat. */
export const StatChip = Stat;

/** Small pill. `solid` = filled (use for counts like unread). Same tones as Tag. */
export function Badge({ children, tone = 'plain', solid, icon, title }: { children: ReactNode; tone?: 'plain' | 'gold' | 'red' | 'green' | 'blue'; solid?: boolean; icon?: ReactNode; title?: string }) {
  return <span className={`badge ${tone} ${solid ? 'solid' : ''}`} title={title}>{icon}{children}</span>;
}

/** Compact dashboard link: icon + label. The one-line hint is a tooltip and an accessible description, not visible clutter. */
export function ActionTile({ to, icon, label, hint, tone = 'blue', badge }: { to: string; icon: ReactNode; label: ReactNode; hint?: ReactNode; tone?: 'blue' | 'gold' | 'red' | 'green'; badge?: ReactNode }) {
  const hintId = useId();
  return (
    <Link to={to} className={`action-tile ${tone}`} title={typeof hint === 'string' ? hint : undefined} aria-describedby={hint ? hintId : undefined}>
      <span className="at-ico" aria-hidden="true">{icon}</span>
      <span className="at-label">{label}</span>
      {hint && <span id={hintId} className="sr-only">{hint}</span>}
      {badge ? <span className="badge solid num">{badge}</span> : null}
    </Link>
  );
}

/**
 * Progressive disclosure. A full-width header button (aria-expanded) with a title, an at-a-glance `summary`
 * (a count or key value, always visible) and a chevron; the body is only rendered while open.
 */
export function Collapsible({ title, summary, icon, defaultOpen = false, children, className = '' }: { title: ReactNode; summary?: ReactNode; icon?: ReactNode; defaultOpen?: boolean; children: ReactNode; className?: string }) {
  const [open, setOpen] = useState(defaultOpen);
  const id = useId();
  return (
    <div className={`collapsible ${open ? 'open' : ''} ${className}`}>
      <button type="button" className="collapsible-head" aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)}>
        {icon && <span className="cl-ico" aria-hidden="true" style={{ display: 'inline-flex' }}>{icon}</span>}
        <span className="collapsible-title">{title}</span>
        {summary !== undefined && summary !== null && <span className="collapsible-summary">{summary}</span>}
        <IconChevron className="collapsible-chev" />
      </button>
      {open && <div className="collapsible-body" id={id} role="region">{children}</div>}
    </div>
  );
}

type BtnProps = {
  children: ReactNode;
  onClick?: () => void;
  /** primary = gold, ghost (default) = blue, danger = red, quiet = neutral panel */
  variant?: 'primary' | 'ghost' | 'danger' | 'quiet';
  icon?: ReactNode;
  disabled?: boolean;
  busy?: boolean;
  type?: 'button' | 'submit';
  block?: boolean;
  small?: boolean;
  title?: string;
};
export function Btn({ children, onClick, variant = 'ghost', disabled, busy, type = 'button', block, small, title, icon }: BtnProps) {
  return (
    <button type={type} title={title} className={`btn ${variant} ${block ? 'block' : ''} ${small ? 'small' : ''}`} onClick={onClick} disabled={disabled || busy}>
      {busy ? <span className="spinner" aria-hidden="true" /> : icon}
      {children}
    </button>
  );
}

export function Bar({ value, max = 20, tone = 'gold', label, showValue = true }: { value: number; max?: number; tone?: 'gold' | 'red' | 'green' | 'blue'; label?: ReactNode; showValue?: boolean }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className="bar-row">
      {label && <span className="bar-label">{label}</span>}
      <span className="bar" role="progressbar" aria-valuenow={value} aria-valuemax={max}>
        <span className={`bar-fill ${tone}`} style={{ width: `${pct}%` }} />
      </span>
      {showValue && <span className="bar-val num">{Number.isInteger(value) ? value : value.toFixed(1)}</span>}
    </div>
  );
}

export function Tag({ children, tone = 'plain', title }: { children: ReactNode; tone?: 'plain' | 'gold' | 'red' | 'green' | 'blue'; title?: string }) {
  return <span className={`tag ${tone}`} title={title}>{children}</span>;
}

export function Loading({ label }: { label?: string }) {
  const { t } = useTranslation();
  return <div className="loading"><span className="spinner big" aria-hidden="true" /><span>{label ?? t('common.loading')}</span></div>;
}

export function ErrorBox({ error, onRetry }: { error?: unknown; onRetry?: () => void }) {
  const { t } = useTranslation();
  const msg = error instanceof Error && error.message !== 'network' ? error.message : t('error.network');
  return (
    <div className="errorbox" role="alert">
      <p>{msg}</p>
      {onRetry && <Btn onClick={onRetry} small>{t('common.retry')}</Btn>}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}

export function Field({ label, children, hint }: { label: ReactNode; children: ReactNode; hint?: ReactNode }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

export function Segmented<T extends string>({ value, options, onChange }: { value: T; options: { value: T; label: ReactNode }[]; onChange: (v: T) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  // Scrollable on narrow phones: keep the selected chip in view (scrolls the strip only, never the page).
  useEffect(() => {
    const box = ref.current;
    const on = box?.querySelector<HTMLElement>('button.on');
    if (!box || !on || box.scrollWidth <= box.clientWidth) return;
    box.scrollTo({ left: on.offsetLeft - (box.clientWidth - on.offsetWidth) / 2, behavior: 'smooth' });
  }, [value]);
  return (
    <div className="segmented" role="tablist" ref={ref}>
      {options.map((o) => (
        <button key={o.value} role="tab" aria-selected={o.value === value} className={o.value === value ? 'on' : ''} onClick={() => onChange(o.value)} type="button">{o.label}</button>
      ))}
    </div>
  );
}

/** Modal on desktop, bottom sheet on phones. */
/** `fixed`: constant-height column; the body no longer scrolls, so children lay out their own header / scrolling list / footer. */
export function Sheet({ open, onClose, title, children, fixed }: { open: boolean; onClose: () => void; title?: ReactNode; children: ReactNode; fixed?: boolean }) {
  const { t } = useTranslation();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="sheet-backdrop" onClick={onClose}>
      <div className={`sheet${fixed ? ' fixed' : ''}`} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-grip" aria-hidden="true" />
        <header className="sheet-head">
          <h3>{title}</h3>
          <button className="icon-btn" onClick={onClose} aria-label={t('common.close')} type="button">
            <svg viewBox="0 0 20 20" width="20" height="20"><path d="M4 4l12 12M16 4L4 16" stroke="currentColor" strokeWidth="2.4" fill="none" strokeLinecap="round" /></svg>
          </button>
        </header>
        <div className="sheet-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

export function ConfirmRow({ message, onConfirm, onCancel, busy }: { message: string; onConfirm: () => void; onCancel: () => void; busy?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="confirm">
      <p>{message}</p>
      <div className="row">
        <Btn variant="danger" onClick={onConfirm} busy={busy}>{t('common.confirm')}</Btn>
        <Btn onClick={onCancel}>{t('common.cancel')}</Btn>
      </div>
    </div>
  );
}

export function Inline({ error }: { error: string | null }) {
  if (!error) return null;
  return <p className="inline-error" role="alert">{error}</p>;
}
